import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { getRatings, rateSkill } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import type { RatingsSummary } from "@/lib/types";

export default function RatingStars({
  slug,
  onRated,
}: {
  slug: string;
  onRated?: (summary: RatingsSummary) => void;
}) {
  const { user, accessToken } = useAuth();
  const pathname = useLocation().pathname;
  const [summary, setSummary] = useState<RatingsSummary | null>(null);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getRatings(slug).then(setSummary).catch(() => setSummary(null));
  }, [slug]);

  async function submit() {
    if (!accessToken || stars < 1) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await rateSkill(slug, { stars, comment: comment || undefined }, accessToken);
      setSummary(updated);
      setComment("");
      onRated?.(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to rate");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rounded-xl p-5"
      style={{
        background: "var(--bg-elev)",
        border: "1px solid var(--border)",
      }}
    >
      <div className="flex items-center gap-2">
        {summary && summary.count > 0 ? (
          <>
            <span className="text-lg text-accent">★ {summary.avg.toFixed(1)}</span>
            <span className="text-sm" style={{ color: "var(--text-muted)" }}>
              {summary.count} rating{summary.count === 1 ? "" : "s"}
            </span>
          </>
        ) : (
          <span className="text-sm" style={{ color: "var(--text-muted)" }}>No ratings yet</span>
        )}
      </div>

      {user ? (
        <div className="mt-3">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                onClick={() => setStars(n)}
                aria-label={`${n} stars`}
                className="text-2xl transition-transform hover:scale-110"
                style={{ color: n <= stars ? "var(--brand-amber)" : "var(--text-muted)" }}
              >
                ★
              </button>
            ))}
          </div>
          <textarea
            placeholder="Optional comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            className="mt-3 w-full rounded-lg px-3 py-2 text-sm outline-none transition"
            style={{
              background: "var(--bg-subtle)",
              border: "1px solid var(--border-strong)",
              color: "var(--text)",
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "var(--brand)")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "var(--border-strong)")}
          />
          {error && <p className="mt-1 text-sm text-red-400">{error}</p>}
          <button
            onClick={submit}
            disabled={busy || stars < 1}
            className="mt-3 rounded-lg bg-primary px-4 py-1.5 text-sm font-semibold text-white shadow-btn transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "Saving…" : "Submit rating"}
          </button>
        </div>
      ) : (
        // A muted sentence here read as just another review — same size, same colour as the
        // comments listed below it. Make it unmistakably an action instead of a line of prose.
        <Link
          to={`/login?next=${encodeURIComponent(pathname)}`}
          className="mt-3 inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-semibold transition hover:opacity-85"
          style={{ borderColor: "var(--brand)", color: "var(--brand)", background: "var(--brand-tint)" }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
          </svg>
          Log in to rate
        </Link>
      )}

      {summary && summary.ratings.length > 0 && (
        <div className="mt-5 border-t pt-4" style={{ borderColor: "var(--border)" }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em]" style={{ color: "var(--text-muted)" }}>
            {summary.count === 1 ? "Review" : "Reviews"}
          </p>
          <ul className="mt-3 space-y-3.5">
            {summary.ratings.slice(0, 5).map((r) => (
              <li key={r.id}>
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-[10px] font-semibold text-accent">
                    {(r.display_name || "?").charAt(0).toUpperCase()}
                  </span>
                  <span className="text-sm font-medium" style={{ color: "var(--text)" }}>
                    {r.display_name || "anon"}
                  </span>
                  <span className="text-xs" style={{ color: "var(--brand-amber)" }}>
                    {"★".repeat(r.stars)}
                    <span style={{ color: "var(--text-muted)" }}>{"★".repeat(5 - r.stars)}</span>
                  </span>
                </div>
                {r.comment && (
                  <p className="mt-1 pl-8 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {r.comment}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
